import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Paperclip, Server } from 'lucide-react';
import { ticketsApi, type TicketPriority } from '../api/tickets';
import { attachmentsApi } from '../api/attachments';
import { orgsApi, type Installation } from '../api/organizations';
import { OrgCombobox } from '../components/OrgCombobox';
import { useAuth } from '../lib/auth';

export default function TicketCreate() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.type === 'staff';

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('P3');
  // Поля только для staff: явно указать клиента и автора (если создаём от его имени).
  // staff подбирает orgId через combobox; client'ы — берётся из user.organization_id.
  const [orgId, setOrgId] = useState<string | null>(
    isStaff ? null : (user?.organization_id || null)
  );
  const [orgContactSnapshot, setOrgContactSnapshot] = useState<{ name: string; email: string } | null>(null);
  // Инсталляции выбранной организации; меняются при смене orgId.
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [installationId, setInstallationId] = useState<string | null>(null);
  const [requesterName, setRequesterName] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  // Аттачи — список выбранных файлов до submit
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const onPickOrg = (id: string | null, org: any) => {
    setOrgId(id);
    setInstallationId(null);  // сбрасываем выбор инсталляции при смене клиента
    if (org) {
      setOrgContactSnapshot({
        name: org.contact_person || '',
        email: org.contact_email || '',
      });
      // Автоподстановка автора из контакт-лица организации (можно перебить).
      if (!requesterName && org.contact_person) setRequesterName(org.contact_person);
      if (!requesterEmail && org.contact_email) setRequesterEmail(org.contact_email);
    } else {
      setOrgContactSnapshot(null);
    }
  };

  // Подгружаем инсталляции при смене orgId. Клиент видит свои.
  useEffect(() => {
    if (!orgId) { setInstallations([]); return; }
    let alive = true;
    orgsApi.installations(orgId)
      .then(list => { if (alive) {
        setInstallations(list);
        // Если у клиента ровно одна инсталляция — выбираем по умолчанию.
        if (list.length === 1) setInstallationId(list[0].id);
      }})
      .catch(() => { if (alive) setInstallations([]); });
    return () => { alive = false; };
  }, [orgId]);

  const onPickFiles = (fs: FileList | null) => {
    if (!fs) return;
    setFiles(prev => [...prev, ...Array.from(fs)]);
  };

  const removeFile = (i: number) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    setProgress(null);
    try {
      // 1. Создаём тикет.
      setProgress('Создаю тикет…');
      const payload: any = { subject, body, priority };
      if (isStaff) {
        if (orgId) payload.organization_id = orgId;
        if (requesterName) payload.requester_name = requesterName;
        if (requesterEmail) payload.requester_email = requesterEmail;
      }
      if (installationId) payload.installation_id = installationId;
      const t = await ticketsApi.create(payload);

      // 2. Прикрепляем файлы (если есть).
      for (let i = 0; i < files.length; i++) {
        setProgress(`Загружаю файл ${i + 1}/${files.length} (${files[i].name})…`);
        await attachmentsApi.upload('ticket', t.id, files[i]);
      }
      nav(`/tickets/${t.id}`);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => nav(-1)} className="text-slate-400 hover:text-white text-sm mb-4 flex items-center gap-1">
        <ArrowLeft size={14} /> Назад
      </button>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Новый тикет</h2>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Тема *">
            <input value={subject} onChange={e => setSubject(e.target.value)} required maxLength={255}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
          </Field>

          <Field label="Описание">
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} maxLength={20000}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
          </Field>

          <Field label="Приоритет">
            <select value={priority} onChange={e => setPriority(e.target.value as TicketPriority)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none">
              <option value="P1">P1 — Critical (FRT 15 мин)</option>
              <option value="P2">P2 — High (FRT 1 ч)</option>
              <option value="P3">P3 — Normal (FRT 4 ч)</option>
              <option value="P4">P4 — Low (FRT 24 ч)</option>
            </select>
          </Field>

          {isStaff ? (
            <>
              <Field label="Клиент">
                <OrgCombobox value={orgId} onChange={onPickOrg} placeholder="Найти клиента (имя или ИНН)…" clearable />
                {orgContactSnapshot && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Контакт-лицо: {orgContactSnapshot.name || '—'} · {orgContactSnapshot.email || '—'}
                  </div>
                )}
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Автор тикета (имя)">
                  <input value={requesterName} onChange={e => setRequesterName(e.target.value)} maxLength={255}
                    placeholder={orgContactSnapshot?.name || ''}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
                </Field>
                <Field label="Email автора">
                  <input type="email" value={requesterEmail} onChange={e => setRequesterEmail(e.target.value)}
                    placeholder={orgContactSnapshot?.email || ''}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
                </Field>
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500">
              Тикет автоматически привяжется к вашей организации, автор — вы ({user?.full_name || user?.email}).
            </div>
          )}

          {/* Инсталляция — показываем только когда есть orgId и хотя бы одна инсталляция */}
          {orgId && installations.length > 0 && (
            <Field label={<><Server size={12} className="inline mr-1" /> Инсталляция</>}>
              <select value={installationId || ''} onChange={e => setInstallationId(e.target.value || null)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none">
                <option value="">— не указывать —</option>
                {installations.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.name}{i.product ? ` · ${i.product}` : ''}{i.version ? ` ${i.version}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {/* Attachments при создании */}
          <Field label={<><Paperclip size={12} className="inline mr-1" /> Прикрепить файлы</>}>
            <label className="block border-2 border-dashed border-slate-700 rounded-lg p-3 text-center hover:border-blue-500 transition cursor-pointer">
              <input type="file" multiple onChange={e => onPickFiles(e.target.files)} className="hidden" />
              <span className="text-xs text-slate-400">Кликни или перетащи файлы</span>
            </label>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs">
                    <span className="text-slate-300 truncate">{f.name}</span>
                    <span className="text-slate-500 ml-2">{(f.size / 1024).toFixed(1)} КБ</span>
                    <button type="button" onClick={() => removeFile(i)} className="ml-2 text-slate-500 hover:text-red-400">×</button>
                  </li>
                ))}
              </ul>
            )}
          </Field>

          {progress && <div className="text-xs text-blue-400">{progress}</div>}
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}

          <button type="submit" disabled={submitting || !subject.trim()}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold px-6 py-2 rounded transition flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? 'Создаю…' : 'Создать тикет'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</span>
      {children}
    </label>
  );
}
